/* tslint:disable:no-multiple-render-methods */
/**
 * Typeahead is a special case, due to the fact that it is a UITK component,
 * and there are many clients consuming it.
 */

import * as React from 'react';
import { inject, observer } from 'mobx-react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { ContextStore, QueryRoute, RouteStore } from 'bernie-core';
import { Layer } from 'bernie-view';

import { ESSAdapter, TypeaheadToolbarVersion, UitkTypeahead } from 'uitk-react-typeahead';

import { FormFieldBinding } from './form-field-binding';
import { SearchFormFieldProps } from 'components/common/search-form';
import { LocalizedText } from 'bernie-l10n';

import { EssClientLogger } from 'typeahead-client-logger';
import { ErrorBoundary } from 'bernie-view';

export interface HotelNameProps extends SearchFormFieldProps {
  context?: ContextStore;
  routeStore?: RouteStore;
  regionId?: string;
  analytics?: any;
}

@FormFieldBinding
@inject('context', 'routeStore', 'analytics')
@observer
export class HotelName extends React.Component<HotelNameProps> {
  private inputRef;
  private essAnalyticsLogger: EssClientLogger;
  public constructor(props) {
    super(props);
    this.inputRef = React.createRef();

    this.dismissTypeahead = this.dismissTypeahead.bind(this);
    this.openTypeahead = this.openTypeahead.bind(this);
    this.updateTypeahead = this.updateTypeahead.bind(this);
    this.getHotelNameFilterDialog = this.getHotelNameFilterDialog.bind(this);
    this.sendTracking = this.sendTracking.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.essAnalyticsLogger = new EssClientLogger(props.analytics);
  }

  private handleClear() {
    const { updateModel } = this.props;

    if (typeof updateModel === 'function') {
      updateModel({
        target: {
          name: 'hotelName',
          value: ''
        }
      });
    }
  }

  public render() {
    const { context, model, modelKey, sortAndFilterOptions } = this.props;
    const value = (model && model[modelKey]) || '';
    const regionId = model && model.regionId;
    const hotelName = sortAndFilterOptions && sortAndFilterOptions[modelKey];
    const label = hotelName && hotelName.label;
    const placeholder = hotelName && hotelName.placeholder;

    if (!hotelName) {
      return null;
    }

    return (
      <React.Fragment>
        <fieldset className="uitk-card-separator-bottom all-b-padding-six">
          <legend className="uitk-cell all-t-padding-six uitk-card-separator-top filter-title uitk-type-heading-500 uitk-type-bold">
            {label}
          </legend>
          <ErrorBoundary label="hotel-name.typeahead">
            <AbacusExperiment name="PWA_Typeahead_Toolbar_V2_Enable">
              <AbacusControl>
                <UitkTypeahead
                  adapter={
                    new ESSAdapter({
                      client: 'Hotels.Search',
                      dest: true,
                      essLogger: this.essAnalyticsLogger,
                      features: 'ta_hierarchy|postal_code|contextual_ta',
                      guid: context && context.deviceId,
                      lob: 'HOTELS',
                      locale: context && context.locale,
                      maxResults: 10,
                      regionId: parseInt(regionId, 10),
                      regionType: 128,
                      siteId: context && context.site && context.site.id
                    })
                  }
                  allowExactMatch={true}
                  icon="search"
                  name="hotelName"
                  label={placeholder}
                  handleClear={this.handleClear}
                  hasClearButton={true}
                  onClick={this.openTypeahead}
                  onDismiss={this.dismissTypeahead}
                  onUpdate={this.updateTypeahead}
                  renderDialog={this.getHotelNameFilterDialog}
                  value={value}
                  toolbarVersion={TypeaheadToolbarVersion.V1}
                  typeaheadPlaceholder={placeholder}
                  ref={this.inputRef}
                />
              </AbacusControl>
              <AbacusVariant bucket={1}>
                <UitkTypeahead
                  adapter={
                    new ESSAdapter({
                      client: 'Hotels.Search',
                      dest: true,
                      essLogger: this.essAnalyticsLogger,
                      features: 'ta_hierarchy|postal_code|contextual_ta',
                      guid: context && context.deviceId,
                      lob: 'HOTELS',
                      locale: context && context.locale,
                      maxResults: 10,
                      regionId: parseInt(regionId, 10),
                      regionType: 128,
                      siteId: context && context.site && context.site.id
                    })
                  }
                  allowExactMatch={true}
                  icon="search"
                  name="hotelName"
                  label={placeholder}
                  handleClear={this.handleClear}
                  hasClearButton={true}
                  onClick={this.openTypeahead}
                  onDismiss={this.dismissTypeahead}
                  onUpdate={this.updateTypeahead}
                  renderDialog={this.getHotelNameFilterDialog}
                  value={value}
                  toolbarVersion={TypeaheadToolbarVersion.V2}
                  typeaheadPlaceholder={placeholder}
                  ref={this.inputRef}
                />
              </AbacusVariant>
            </AbacusExperiment>
          </ErrorBoundary>
        </fieldset>
        <h1 className="filter-title uitk-type-heading-500 uitk-type-bold all-b-padding-six">
          <LocalizedText message="hotels.searchResults.filtersDialog.hotelName.label" />
        </h1>
      </React.Fragment>
    );
  }

  private dismissTypeahead() {
    this.props.routeStore.goBack();
    this.essAnalyticsLogger.logTypeaheadBlurEvent();
    if (this.inputRef && this.inputRef.current && typeof this.inputRef.current.focusTypeaheadInput === 'function') {
      this.inputRef.current.focusTypeaheadInput();
    }
  }

  private openTypeahead() {
    const { routeStore } = this.props;

    if (routeStore && typeof routeStore.updateSearch === 'function') {
      routeStore.updateSearch({ pwaDialogNested: 'hotel-name-typeahead' });
    }
  }

  private updateTypeahead(selection) {
    const term = selection.term;

    this.props.updateModel({
      target: {
        name: 'hotelName',
        value: term
      }
    });
    this.sendTracking(term);
  }

  private sendTracking(term) {
    this.props.analytics.clickTracker(`.hotelName.${term}`, `hotelName.${term}`)();
  }

  private getHotelNameFilterDialog(children) {
    return (
      <QueryRoute query={{ pwaDialogNested: 'hotel-name-typeahead' }}>
        <Layer id="hotel-name-typeahead" onDismiss={this.dismissTypeahead}>
          {children}
        </Layer>
      </QueryRoute>
    );
  }
}
